<script setup>
import { computed } from 'vue';
import { intlLocale, t } from '../i18n';
import { isPhone } from '../isPhone';
import { plural } from '../operations';
import { PANEL } from '../ui';
import { formatLevel, formatRub } from '../achievements';

// Block 4 of «Достижения» (docs/achievements/SPEC.md, «Проект v2»): what depends on the person — top-ups every
// month, passive income, drawdowns lived through, diversity. Rows on a phone, tiles 2 × 2 on a desktop; each
// opens its levels in a sheet.
const props = defineProps({
  habits: {
    type: Object,
    required: true,
  },
  // Sums by security are counted yet: diversity waits
  breakdown: {
    type: Boolean,
    default: true,
  },
  today: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['open']);

function percent(share) {
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1 }).format(share * 100);
}

const rows = computed(() => {
  const { streak, income, drawdown, diversity } = props.habits;
  const lastDay = new Date(Date.UTC(Number(props.today.slice(0, 4)), Number(props.today.slice(5, 7)), 0)).getUTCDate();

  let streakNote;
  if (!streak.month_done && streak.current > 0) {
    streakNote = t('achievements.streakWaiting', { day: lastDay });
  } else if (streak.current > 0 && streak.current === streak.best && streak.next) {
    streakNote = t('achievements.streakBestNext', { level: streak.next, left: streak.next - streak.current });
  } else if (streak.best > streak.current) {
    streakNote = t('achievements.streakBest', { best: streak.best });
  } else {
    streakNote = t('achievements.streakRule');
  }

  const deepest = drawdown.episodes.length > 0 ? Math.max(...drawdown.episodes.map((e) => e.depth)) : null;
  let drawdownNote;
  if (drawdown.ongoing) {
    drawdownNote = t('achievements.drawdownNow', { depth: percent(drawdown.ongoing.depth), sum: formatLevel(Math.max(0, drawdown.ongoing.invested)) });
  } else if (deepest !== null) {
    drawdownNote = t('achievements.drawdownLived');
  } else {
    drawdownNote = t('achievements.drawdownNone');
  }

  return [
    {
      key: 'streak',
      title: t('achievements.habit.streak'),
      value: streak.current > 0 ? t('achievements.monthsShort', { count: streak.current }) : '—',
      note: streakNote,
    },
    {
      key: 'income',
      title: t('achievements.habit.income'),
      value: income.last12 > 0 ? t('achievements.perMonth', { sum: formatRub(income.per_month) }) : '—',
      note: income.months < 12
        ? plural(income.months, 'achievements.incomeMonths')
        : t('achievements.incomeYear'),
    },
    {
      key: 'drawdown',
      title: t('achievements.habit.drawdown'),
      value: deepest !== null ? `−${percent(deepest)} %` : '—',
      note: drawdownNote,
    },
    {
      key: 'diversity',
      title: t('achievements.habit.diversity'),
      pending: !props.breakdown || !diversity,
      value: diversity ? plural(diversity.sectors.count, 'achievements.sectorsCount') : '',
      note: diversity?.largest
        ? t('achievements.largestShare', { ticker: diversity.largest.ticker ?? '—', share: percent(diversity.largest.share) })
        : '',
    },
  ];
});
</script>

<template>
  <section :class="PANEL">
    <h3 class="text-[1.0625rem] font-semibold text-slate-100 sm:mb-3 sm:text-base">
      {{ t('achievements.habitsTitle') }}
    </h3>
    <div :class="isPhone ? 'mt-1' : 'grid grid-cols-2 gap-3'">
      <button
        v-for="row in rows"
        :key="row.key"
        type="button"
        :disabled="row.pending"
        class="flex w-full min-w-0 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 disabled:cursor-default"
        :class="isPhone ? 'min-h-14 border-t border-slate-800 py-3 first:border-t-0' : 'rounded-lg bg-slate-800/40 p-4 hover:bg-slate-800/70'"
        @click="emit('open', row.key)"
      >
        <span class="min-w-0 flex-1">
          <span class="flex items-baseline justify-between gap-2">
            <span
              class="truncate text-[0.9375rem] sm:text-sm"
              :class="row.pending ? 'text-slate-500' : 'text-slate-100'"
            >{{ row.title }}</span>
            <span
              class="shrink-0 text-[0.9375rem] tabular-nums sm:text-sm"
              :class="row.pending ? 'text-slate-500' : 'font-medium text-slate-100'"
            >{{ row.pending ? t('achievements.counting') : row.value }}</span>
          </span>
          <span
            v-if="!row.pending && row.note"
            class="mt-0.5 block text-[0.8125rem] text-slate-400"
          >{{ row.note }}</span>
        </span>
        <span
          v-if="!row.pending"
          class="shrink-0 text-lg leading-none text-slate-500"
          aria-hidden="true"
        >›</span>
      </button>
    </div>
  </section>
</template>
